import { networkLogos } from '../networkLogos'
import { ratePlanCodes } from '../ratePlanCode'
import { commonCtaText } from 'src/modules/utils/constants'
import { createNetworkLogo, formatListText } from 'src/modules/utils/plans'
import { channelIds } from 'src/modules/hooks/useChannelSelector'
import { PlanIntervals } from '.'
import { CashAppLogo } from 'src/components/icons/CashApp'
import { colors } from '../marketing-ds'

export const laPriceMonthly = 14.99
export const laDiscountPriceMonthly = 9.99
export const laChannelCount = 50

export const LA_COMMON_LIST_ITEMS = {
  en: {
    channels: `${laChannelCount}+ channels of live TV and sports`,
    dvr: 'Unlimited Cloud DVR',
    cashApp: <CashAppLogo text="Now accepting" isDark={true} />,
  },
  es: {
    channels: `Más de ${laChannelCount} canales de TV y deportes en vivo`,
    dvr: 'Espacio ilimitado en tu Cloud DVR',
    cashApp: <CashAppLogo text="Ahora aceptamos" isDark={true} />,
  },
}

const price = {
  latino: {
    [PlanIntervals.monthly]: {
      price: laPriceMonthly,
      discountedPrice: laDiscountPriceMonthly,
    },
  },
  masn_plus: {
    [PlanIntervals.monthly]: {
      price: 32.99,
      discountedPrice: 24.99,
    },
  },
}
const LatinoNetworkList = [
  createNetworkLogo(networkLogos.espndeportes, 55, 24),
  {
    logo: 'https://assets-imgx.fubo.tv/marketing/lp/images/networks/fox-deportes-vertical-m.png',
    alt: networkLogos.foxdeportes.alt,
    width: 55,
    height: 24,
  },
  createNetworkLogo(networkLogos.bein, 75, 19),
]

const latinoPlusEquiposLocales = {
  en: {
    plan: 'latino_plus_equipos_locales',
    title: 'Latino Plan + Equipos Locales',
    tagline: 'The best Spanish language TV, live soccer, news, entertainment and more.',
    banner: 'Local NBA and NHL team coverage plus more!',
  },
  es: {
    plan: 'latino_plus_equipos_locales',
    title: 'Latino Plan + Equipos Locales',
    tagline: 'TV en español, fútbol en vivo, telenovelas, noticias y mucho más.',
    banner: 'Cobertura de tu equipo local de la NBA y NHL',
  },
}

const baseData = {
  latino_en_monthly: {
    market: 'latino',
    plan: 'latino',
    channelId: channelIds.latino,
    offerCode: ratePlanCodes.latino,
    title: 'Fubo Latino',
    subtitle: 'TV in Spanish, live soccer and other sports, shows, news, and more.',
    tagline: 'The best Spanish language TV, live soccer, news, entertainment and more.',
    asterisk: true,
    price: price.latino.monthly.price,
    discountedPrice: price.latino.monthly.discountedPrice,
    interval: PlanIntervals.monthly,
    backgroundColor: colors.orange[100],

    listItems: () => [
      { available: true, text: LA_COMMON_LIST_ITEMS.en.channels },
      { available: true, text: LA_COMMON_LIST_ITEMS.en.dvr },
      { available: true, text: '2 Screens at once with Standard Share.' },
      {
        available: true,
        text: 'La Liga, Ligue 1, Serie A, Liga BetPlay, and more sports.',
      },
      {
        available: true,
        text: <CashAppLogo text="Now accepting" />,
      },
    ],
    networkLogos: [...LatinoNetworkList, { alt: '+ MORE' }],
    footnote: () =>
      `*Offer valid for the first month. Renews at $${price.latino.monthly.price}/mo. Additional taxes and fees may apply. Prices subject to change. Cancel anytime.`,
  },
  latino_es_monthly: {
    market: 'latino',
    plan: 'latino',
    channelId: channelIds.latino,
    offerCode: ratePlanCodes.latino,
    title: 'Fubo Latino',
    subtitle: 'TV en español, deportes en vivo, telenovelas, noticias y mucho más.',
    tagline: 'TV en español, fútbol en vivo, telenovelas, noticias y mucho más.',
    networkLogos: [...LatinoNetworkList, { alt: 'Y MÁS!' }],
    asterisk: true,
    price: price.latino.monthly.price,
    discountedPrice: price.latino.monthly.discountedPrice,
    interval: PlanIntervals.monthly,
    listItems: () => [
      { available: true, text: LA_COMMON_LIST_ITEMS.es.channels },
      { available: true, text: LA_COMMON_LIST_ITEMS.es.dvr },
      { available: true, text: '2 Pantallas a la vez con Standard Share' },
      { available: true, text: 'La Liga, Ligue 1, Serie A, Liga BetPlay y mucho más' },
      {
        available: true,
        text: <CashAppLogo text="Ahora aceptamos" />,
      },
    ],
    footnote: () =>
      `*Oferta válida solo para el primer mes. Se renueva a $${price.latino.monthly.price}/mes. Pueden aplicarse impuestos y otros cargos. Precios sujetos a cambios. Cancela cuando quieras.`,
  },
  latino_en_standalone_monthly: {
    market: 'latino',
    channelId: channelIds.latino,
    interval: PlanIntervals.monthly,
    asterisk: true,
    subtitle: null,
    listItems: (data) => [
      {
        available: true,
        text: formatListText(data?.rsn, 'en'),
      },
      {
        available: true,
        text: formatListText(data?.teams, 'en'),
      },
      { available: true, text: LA_COMMON_LIST_ITEMS.en.channels },
      { available: true, text: LA_COMMON_LIST_ITEMS.en.dvr },
      {
        available: true,
        text: LA_COMMON_LIST_ITEMS.en.cashApp,
      },
    ],
    footnote: (data) =>
      `*Offer valid for the first month. Renews at $${data.price}/mo. Additional taxes and fees may apply. Prices subject to change. Cancel anytime. ${data.rsnName ? `${data.rsnName} broadcasts in English only.` : ''}`,

    ctaText: commonCtaText.startForFree,
  },
  latino_es_standalone_monthly: {
    market: 'latino',
    channelId: channelIds.latino,
    interval: PlanIntervals.monthly,
    asterisk: true,
    subtitle: null,
    listItems: (data) => [
      {
        available: true,
        text: formatListText(data?.rsn, 'es'),
      },
      {
        available: true,
        text: formatListText(data?.teams, 'es'),
      },
      { available: true, text: LA_COMMON_LIST_ITEMS.es.channels },
      { available: true, text: LA_COMMON_LIST_ITEMS.es.dvr },
      {
        available: true,
        text: LA_COMMON_LIST_ITEMS.es.cashApp,
      },
    ],
    footnote: (data) =>
      `*Oferta válida solo para el primer mes. Se renueva a $${data.price}/mes. Pueden aplicarse impuestos y otros cargos. Precios sujetos a cambios. Cancela cuando quieras. ${data.rsnName ? `${data.rsnName} ofrece contenido solo en inglés.` : ''}`,

    ctaText: commonCtaText.pruebaloGratis,
  },
}

export const latinoPlanData = {
  latino_en_monthly: baseData.latino_en_monthly,
  latino_es_monthly: baseData.latino_es_monthly,
  latino_en_standalone_monthly: {
    ...baseData.latino_en_monthly,
    ...baseData.latino_en_standalone_monthly,
    fillImageSpace: true,
    backgroundColor: '#ff3b04',
    listItems: (data) => [
      {
        available: false,
        text: formatListText(data?.rsn, 'en'),
      },
      {
        available: false,
        text: formatListText(data?.teams, 'en'),
      },
      { available: true, text: LA_COMMON_LIST_ITEMS.en.channels },
      { available: true, text: LA_COMMON_LIST_ITEMS.en.dvr },
      {
        available: true,
        text: LA_COMMON_LIST_ITEMS.en.cashApp,
      },
    ],
  },
  latino_es_standalone_monthly: {
    ...baseData.latino_es_monthly,
    ...baseData.latino_es_standalone_monthly,
    fillImageSpace: true,
    backgroundColor: '#ff3b04',
    listItems: (data) => [
      {
        available: false,
        text: formatListText(data?.rsn, 'es'),
      },
      {
        available: false,
        text: formatListText(data?.teams, 'es'),
      },
      { available: true, text: LA_COMMON_LIST_ITEMS.es.channels },
      { available: true, text: LA_COMMON_LIST_ITEMS.es.dvr },
      {
        available: true,
        text: LA_COMMON_LIST_ITEMS.es.cashApp,
      },
    ],
  },
  latino_soccer_en_monthly: {
    ...baseData.latino_en_monthly,
    promoImg: 'https://assets-imgx.fubo.tv/marketing/lp/images/posters/latino-soccer-plan-card.png',
    subtitle: null,
    listItems: () => [
      { available: true, text: 'Soccer matches in Spanish' },
      { available: true, text: LA_COMMON_LIST_ITEMS.en.channels },
      { available: true, text: 'Access to MLB playoffs, MNF and NHL games' },
      { available: true, text: LA_COMMON_LIST_ITEMS.en.dvr },
      { available: true, text: LA_COMMON_LIST_ITEMS.en.cashApp },
      {},
    ],
    networkLogos: [
      ...LatinoNetworkList,
      createNetworkLogo(networkLogos.tyc, 75, 19),
      { alt: '+MORE' },
    ],
  },
  latino_soccer_es_monthly: {
    ...baseData.latino_es_monthly,
    promoImg: 'https://assets-imgx.fubo.tv/marketing/lp/images/posters/latino-soccer-plan-card.png',
    subtitle: null,
    listItems: () => [
      { available: true, text: 'Partidos de fútbol en español' },
      { available: true, text: LA_COMMON_LIST_ITEMS.es.channels },
      { available: true, text: 'Acceso a partidos de MLB playoffs, MNF y NHL' },
      { available: true, text: LA_COMMON_LIST_ITEMS.es.dvr },
      { available: true, text: LA_COMMON_LIST_ITEMS.es.cashApp },
      {},
    ],
    networkLogos: [
      ...LatinoNetworkList,
      createNetworkLogo(networkLogos.tyc, 75, 19),
      { alt: 'Y MÁS' },
    ],
  },

  //Fanduel Baseball Plan, promoImg is from fanduel data: useCheckFanduelRSN()
  latino_plus_equipos_locales_en_monthly: {
    ...baseData.latino_en_standalone_monthly,
    ...latinoPlusEquiposLocales.en,
    rsnName: 'FanDuel Sports Network', // Used in the subheader and footnote for standalone plans
    promoImg:
      'https://assets-imgx.fubo.tv/marketing/lp/images/card/rsn_basketball_hockey_plan_card.jpg',
    networkLogos: [
      {
        logo: 'https://assets-imgx.fubo.tv/marketing/lp/images/networks/fanduel-horizontal-c-v2.png',
        alt: networkLogos.fanduelsports.alt,
        width: 44,
        height: 44,
      },
      ...LatinoNetworkList,
      { alt: '+MORE' },
    ],
    ctaText: commonCtaText.startWatchingForFree,
  },
  latino_plus_equipos_locales_es_monthly: {
    ...baseData.latino_es_standalone_monthly,
    ...latinoPlusEquiposLocales.es,
    rsnName: 'FanDuel Sports Network',
    promoImg:
      'https://assets-imgx.fubo.tv/marketing/lp/images/card/rsn_basketball_hockey_plan_card.jpg',
    networkLogos: [
      {
        logo: 'https://assets-imgx.fubo.tv/marketing/lp/images/networks/fanduel-horizontal-c-v2.png',
        alt: networkLogos.fanduelsports.alt,
        width: 44,
        height: 44,
      },
      ...LatinoNetworkList,
      { alt: 'Y MÁS' },
    ],
    ctaText: commonCtaText.pruebaloGratis,
  },
  //MASN+
  latino_masn_en_monthly: {
    ...baseData.latino_en_standalone_monthly,
    ...latinoPlusEquiposLocales.en,
    promoImg:
      'https://assets-imgx.fubo.tv/marketing/lp/images/card/rsn_basketball_hockey_plan_card.jpg',
    plan: 'latino_plus_equipos_locales masn+',
    rsnName: 'MASN+', // Used in the subheader and footnote for standalone plans
    offerCode: ratePlanCodes.latino_masn_plus,
    ctaText: commonCtaText.startWatching,
    teams: ['Baltimore Orioles', 'Washington Nationals'],
    rsn: ['MASN+'],
    price: price.masn_plus.monthly.price,
    discountedPrice: price.masn_plus.monthly.discountedPrice,
    networkLogos: [
      createNetworkLogo(networkLogos.masn, 85, 48),
      ...LatinoNetworkList,
      { alt: '+MORE' },
    ],
    footnote: (data) =>
      `*Offer valid for the first month. Renews at $${data.price}/mo. Additional taxes and fees may apply. Prices subject to change. No free trial with this plan. Cancel anytime. ${data.rsnName ? `${data.rsnName} broadcasts in English only.` : ''}`,
  },
  latino_masn_es_monthly: {
    ...baseData.latino_es_standalone_monthly,
    ...latinoPlusEquiposLocales.es,
    promoImg:
      'https://assets-imgx.fubo.tv/marketing/lp/images/card/rsn_basketball_hockey_plan_card.jpg',
    rsnName: 'MASN+', // Used in the subheader and footnote for standalone plans
    rsn: ['MASN+'],
    plan: 'latino_plus_equipos_locales masn+',
    offerCode: ratePlanCodes.latino_masn_plus,
    ctaText: commonCtaText.accederAhora,
    teams: ['Baltimore Orioles', 'Washington Nationals'],
    price: price.masn_plus.monthly.price,
    discountedPrice: price.masn_plus.monthly.discountedPrice,
    networkLogos: [
      createNetworkLogo(networkLogos.masn, 85, 48),
      ...LatinoNetworkList,
      { alt: 'Y MÁS' },
    ],
    footnote: (data) =>
      `*Oferta válida solo para el primer mes. Se renueva a $${data.price}/mes. Pueden aplicarse impuestos y otros cargos. Precios sujetos a cambios. No hay prueba gratuita con este plan. Cancela cuando quieras. ${data.rsnName ? `${data.rsnName} ofrece contenido solo en inglés.` : ''}`,
  },
}
